<template>
  <message-box
    v-if="currentAction === 'comments'"
    title="New internal message"
    type="orange"
    submit-text="Send internal message"
    :disable-submit="disableSubmit"
    @on-close="setCurrentAction('')"
    @on-cancel="setCurrentAction('')"
    @on-submit="handleSubmitMessage"
  >
    <div class="message-contents-field">
      <NrSelect
        :modelValue="selectedNotifiedUsers"
        placeholder="Type or select a user to notify"
        :options="notifiableUsers"
        @option:selecting="selectUsersToNotify"
        @option:deselecting="deselectUsersToNotify"
        class="m-top-12"
        :filterable="true"
        multiple
        required
      >
        <template v-slot:label>Notify a user (required)</template>
      </NrSelect>
      <p
        v-if="notifyUserWarning"
        class="is-size-7 m-bottom-12 notify-user-warning"
      >
        Select a user or group to notify.
      </p>
      <p v-else class="is-size-7 m-bottom-12">
        Only users assigned to this request can be notified. All internal
        messages still post to the timeline.
      </p>
      <NrSelect
        v-if="staffOnlyItems"
        v-model="selectedMessageTemplates"
        placeholder="Search and select message template(s)"
        :options="filteredMessageTemplates"
        :filterable="false"
        :loading="dataFetchState.message_templates.fetching"
        :hasMore="!dataFetchState.message_templates.end"
        @option:deselected="setMessageTemplate(true, $event)"
        @option:selected="setMessageTemplate(false, $event)"
        @open="searchMessageTemplates"
        @search="searchMessageTemplates"
        @load-more="loadMoreMessageTemplates"
        multiple
      >
        <template v-slot:label>Message Template(s)</template>
      </NrSelect>
      <NrFormLabel>Message contents</NrFormLabel>
      <nr-editor
        ref="editor"
        v-model="message"
        :resizable="false"
        :rows="10"
        strike
        link
        bulletList
        orderedList
      />
      <p class="is-size-7 m-bottom-12 m-top-4">
        Send an internal message to selected staff on the request. This message
        will not be visible to the requester.
      </p>
    </div>
  </message-box>
</template>

<script lang="ts">
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import MessageBox from '@nextrequestco/components/src/components/MessageBox'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements'
import { mapActions, mapState } from 'vuex'
import { MessageTemplates } from './types/message_boxes'
import throttle from 'lodash/throttle'
import uniqBy from 'lodash/uniqBy'

interface RequestStaff {
  id: number
  // eslint-disable-next-line camelcase
  name_with_email: string
}

interface UserOption {
  value: number
  label: string
}

const ALL_ASSIGNED_STAFF = {
  value: 0,
  label: 'All assigned staff'
}

export default {
  name: 'InternalMessageModal',
  components: { MessageBox, NrSelect, 'nr-editor': NrEditor, NrFormLabel },
  data() {
    return {
      selectedMessageTemplates: [],
      message: '',
      messageRequired: false,
      selectedNotifiedUsers: [ALL_ASSIGNED_STAFF]
    }
  },
  watch: {
    currentAction(newVal) {
      if (newVal === 'comments') {
        this.resetData()
      }
    },
    request(newVal, oldVal) {
      if (newVal.pretty_id !== oldVal.pretty_id) {
        this.resetNotifiedUsers()
      }
    }
  },
  computed: {
    ...mapState('request_show', [
      'displayMsgBox',
      'currentAction',
      'dataFetchState',
      'dataOptions',
      'request',
      'requestStaff'
    ]),

    notifyUserWarning(): boolean {
      return this.selectedNotifiedUsers.length === 0
    },

    disableSubmit(): boolean {
      return !this.selectedNotifiedUsers.length || !this.message.length
    },

    staffOnlyItems(): boolean {
      return this.$ability.can('read', 'staff_only_items')
    },

    notifiableUsers(): UserOption[] {
      const poc = {
        value: this.request.poc.id,
        label: this.request.poc.name_with_email
      }
      const requestStaff = this.requestStaff.map((user: RequestStaff) => ({
        value: user.id,
        label: user.name_with_email
      }))
      return uniqBy([ALL_ASSIGNED_STAFF, poc, ...requestStaff], 'value')
    },

    filteredMessageTemplates(): void {
      return this.dataOptions.message_templates.map(
        (item: MessageTemplates) => ({
          value: item.id,
          label: item.message_template_name,
          description: item.message_template_description
        })
      )
    }
  },
  methods: {
    ...mapActions('request_show', [
      'submitInternalMessage',
      'setDisplayMsgBox',
      'fetchDataOptions',
      'setCurrentAction'
    ]),

    isAllAssignedStaff(val: UserOption): Boolean {
      return val.value === ALL_ASSIGNED_STAFF.value
    },

    resetData() {
      this.resetNotifiedUsers()
      this.selectedMessageTemplates = []
      this.message = ''
      this.messageRequired = false
    },

    deselectUsersToNotify(val: UserOption): void {
      this.selectedNotifiedUsers = this.selectedNotifiedUsers.filter(
        (user: UserOption) => user.value !== val.value
      )
    },

    selectUsersToNotify(val: UserOption): void {
      if (this.isAllAssignedStaff(val)) {
        this.resetNotifiedUsers()
      } else {
        this.selectedNotifiedUsers = [
          ...this.selectedNotifiedUsers,
          val
        ].filter((user: UserOption) => !this.isAllAssignedStaff(user))
      }
    },

    resetNotifiedUsers(): void {
      this.selectedNotifiedUsers = [ALL_ASSIGNED_STAFF]
    },

    setMessageRequired(val: boolean): void {
      this.messageRequired = val
    },

    searchMessageTemplates: throttle(function (searchText = '') {
      this.fetchDataOptions({
        searchText,
        dataType: 'message_templates'
      })
    }, 400),

    loadMoreMessageTemplates: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.message_templates.search_text,
        dataType: 'message_templates'
      })
    }, 400),

    disableSubmitMsg(): boolean {
      if (this.staffOnlyItems) {
        return !(this.messageRequired && this.message.length)
      } else {
        return !this.message.length
      }
    },

    setMessageTemplate(reset: boolean = false, templates = []): void {
      if (reset) {
        this.message = this.selectedMessageTemplates
          .map((temp: { description: string }) => temp.description)
          .join('')
      } else {
        this.message = this.message + templates.pop()?.description
      }
    },

    handleSubmitMessage(): void {
      let payload = {
        note_text: this.message,
        note_type: 'staff',
        state: 'staff',
        request_id: this.request.pretty_id,
        message_template_ids: this.selectedMessageTemplates.map(
          (template: { value: number }) => template.value
        )
      }
      // By default if users are not selected, we will not set the users parameter.
      // If no users parameter is set, the backend will send the message to all assigned staff
      // If users are selected, we pass them into the users parameter
      const allAssignedSelected =
        this.selectedNotifiedUsers.length === 1 &&
        this.isAllAssignedStaff(this.selectedNotifiedUsers[0])
      if (!allAssignedSelected) {
        payload = Object.assign(payload, {
          users: this.selectedNotifiedUsers.map(
            (user: UserOption) => user.value
          )
        })
      }
      this.submitInternalMessage(payload).then(() => {
        this.setCurrentAction('')
      })
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';
.message-box {
  .notify-user-warning {
    color: $red;
  }
  .message-contents-field,
  .editor-field {
    display: flex;
    flex-direction: column;
    flex: 0 1 100%;
    .nr-select {
      margin-bottom: 0.25rem;
    }
  }

  .nr-editor {
    flex: 1 1 auto;
  }
}
</style>
